import React, { FC, useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Alert from 'react-bootstrap/Alert'
import FhirLoader from '../../../components/FhirLoader'
import { Link } from 'react-router-dom'
import useMembers from '../../../hooks/admin/useMembers'
import isAuthenticated from '../../../routes/PrivateRoute'

import { useGlobalState } from '../../../hooks/store/useGlobalState'
import PaginationButtons from '../../../components/PaginationButtons'
import PermissionGate from '../../../components/PermissionGate'
import useUserWithRole from '../../../hooks/authentication/useUserWithRole'
import Identifier from '../../../components/FhirData/DataTypes/Identifier'
import PageTitle from '../../../components/PageTitle'

const initialFormState = {
  category: 'LastName',
  keyword: ''
}

type MembersResponse = {
  members: any
  total: number,
  nextLink?: string,
  previousLink?: string
}

const Members: FC = () => {
  const { user } = useUserWithRole()
  const [formState, setFormState] = useState(initialFormState)
  const [members, setMembers] = useState({
    members: [],
    total: 0,
    nextLink: '',
    previousLink: ''
  } as MembersResponse)

  const [page, setPage] = useState(1)
  const [pageState, setPageState] = useState({
    pagesOffset: '',
    fhirPageState: '',
    pageId: ''
  })

  const setFhirId = useGlobalState((state) => state.setFhirId)

  const { isLoading, data, refetch, isSuccess, isFetching, isError } = useMembers({
    page,
    category: formState.category,
    searchTerm: formState.keyword,
    pagesOffset: pageState.pagesOffset,
    pageState: pageState.fhirPageState,
    pageId: pageState.pageId
  })

  useEffect(() => {
    if (!isFetching && isSuccess) {
      setMembers(data)
    }
  }, [isFetching, isSuccess])

  const handleMemberSearch = (e: any) => {
    e.preventDefault()
    refetch()
  }

  useEffect(() => {
    if (pageState.pagesOffset !== '' || pageState.pageId !== '') {
      refetch()
    }
  }, [pageState.pagesOffset, pageState.pageId])

  const selfLink = data?.foundMembers?.link?.find((l: any) => l.relation === 'self')?.url
  const pageSize = selfLink && Number(new URL(selfLink).searchParams.get('_count'))
  const totalPages = data?.foundMembers?.total
    ? Math.ceil(data.foundMembers.total / pageSize)
    : 1

  const currentPage = pageState.pagesOffset
    ? Math.floor(Number(pageState.pagesOffset) / pageSize) + 1
    : 1

  const handleChangePage = (pageOrUrl: string | number | undefined) => {
    if (!pageOrUrl) return
    let newPages = ''
    let newOffset = ''
    let newPageId = ''

    if (typeof pageOrUrl === 'string') {
      const queryParams = new URLSearchParams(new URL(pageOrUrl).search)
      newPages = queryParams.get('_getpages') || ''
      newOffset = queryParams.get('_getpagesoffset') || '0'
      newPageId = queryParams.get('_pageId') || ''
    } else {
      const link = data?.foundMembers?.link?.find((l: any) =>
        pageOrUrl > currentPage ? l.relation === 'next' : l.relation === 'previous'
      )?.url

      if (link) {
        const urlObj = new URL(link)
        newPages = urlObj.searchParams.get('_getpages') || ''
        newOffset = ((pageOrUrl - 1) * pageSize).toString()
      } else {
        newOffset = ((pageOrUrl - 1) * pageSize).toString()
      }

      setPage(pageOrUrl)
    }
    setPageState(
      {
        fhirPageState: newPages,
        pagesOffset: newOffset,
        pageId: newPageId
      }
    )
  }

  const handleClearSearch = () => {
    setFormState(initialFormState)
    setPageState({
      pagesOffset: '',
      fhirPageState: '',
      pageId: ''
    })
    setMembers({
      members: [],
      total: 0,
      nextLink: '',
      previousLink: ''
    } as MembersResponse)
  }

  return (
    <section>
      <PermissionGate
        user={user}
        role='admin'
      >
        <Container >
          <PageTitle displayName="Members" />
          <h1 className="headerText h2">Members</h1>
          <Form noValidate onSubmit={handleMemberSearch} className="d-flex align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 me-2">
              <Form.Label className="mb-0">Find a Member</Form.Label>
              <Form.Select
                name="category"
                value={formState.category}
                onChange={(e) => setFormState((ps) => ({ ...ps, category: e.target.value }))}
                className="w-auto"
              >
                <option disabled>Select Category</option>
                <option value="LastName">Last Name</option>
                <option value="MemberId">Member ID</option>
              </Form.Select>

              <Form.Control
                type="text"
                name="keyword"
                placeholder="Enter Search Terms"
                value={formState.keyword}
                onChange={(e) => setFormState((ps) => ({ ...ps, keyword: e.target.value }))}
                className="w-auto"
              />
            </div>

            <Button variant="primary" type="submit" disabled={!formState.keyword}>
              Search
            </Button>
            <Button variant="primary-outline" type="reset" onClick={handleClearSearch}>
              Clear
            </Button>
          </Form>

          <hr />
          {isLoading || isFetching ? (
            <div className='d-flex flex-column align-items-center p-2' style={{ backgroundColor: '#e3e3e3' }}>
              <FhirLoader message="Loading Members..." />
            </div>
          ) : (
            <>
              {
                members?.members?.length > 0 && members?.total > 0 && (
                  <div className='my-3 fw-bold'>
                    <span role="status" aria-live="polite">{members.total.toLocaleString()} record(s) found</span>
                    <PaginationButtons
                      totalPages={totalPages}
                      currentPage={currentPage}
                      handleChangePage={handleChangePage}
                    />
                  </div>
                )
              }
              {
                isError && <Alert className='mt-3' variant='danger' >We received an error finding your members.</Alert>
              }
              <Table hover size='sm' className='mt-3'>
                <thead>
                  <tr>
                    <th scope="col">Patient Identifier</th>
                    <th scope="col">Name</th>
                    <th scope="col">FHIR Data</th>
                    <th scope="col">Manage Account</th>
                    <th scope='col'>Account Status</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    isSuccess && members.members.length > 0 ? (
                      <>
                        {
                          members.members.map((member: any) => (
                            <tr key={member.patientId}>
                              <th>
                                <Identifier data={member.patientIdentifier} />
                              </th>
                              <td>{member.firstName || ''} {member.lastName || ''}</td>
                              <td><Link to={'/patient'} onClick={() => setFhirId(member.patientId)}>View Data</Link></td>
                              <td>
                                {
                                  member.hasAccount && (
                                    <Link to={`/account/${member.accountId}`}>Manage Account</Link>
                                  )
                                }
                              </td>
                              <td>
                                {
                                  member.hasAccount ?
                                    member.enabled ? 'Active' : 'Locked' : ''
                                }
                              </td>
                            </tr>
                          ))
                        }
                      </>
                    ) : isSuccess && members.members.length === 0 ? (
                      (
                        <tr>
                          <td scope="row" colSpan={5} className='text-center' style={{ backgroundColor: '#e3e3e3' }}>
                            <span>No records found.</span>
                          </td>
                        </tr>
                      )
                    ) : null
                  }
                </tbody>
              </Table>
              {
                members?.members?.length > 0 && members?.total > 0 && (
                  <div className='my-3 fw-bold'>
                    <PaginationButtons
                      totalPages={totalPages}
                      currentPage={currentPage}
                      handleChangePage={handleChangePage}
                    />
                  </div>
                )
              }
            </>
          )}
        </Container>
      </PermissionGate>
    </section>
  )
}

export default isAuthenticated(Members)